"use client";
import React, { useState } from "react";
import dayjs from "dayjs";
import { Space } from "antd";

import DateSelector from "@/app/components/DateSelector";

import SelectService from "../../../../components/SelectService";
import DisplaySchedules from "../DisplaySchedules";
import { UserData, ServiceOption, Service } from "@/lib/api/types";
import { formatDate, getDatesBetween } from "@/lib/helpers";
import { AppointmentForm, useApp } from "@/lib/hooks/useAppointment";
import { DB_DATE_FORMAT } from "@/consts";
import type { EditionBusinessKey } from "@/consts/businesses";
import type { BookingCancellationPolicy } from "@/lib/api/bookingCancellationPolicy";
import type { CustomerBookingWindow } from "@/lib/api/bookingWindow";
import { resolveBusinessForAppointment } from "@/lib/api/openingsLocations";
import type { StylistTag } from "@/lib/stylists";
import {
  clampDateToBookingWindow,
  CustomerBookingHorizonNote,
  customerBookingWindowMaxDate,
  MinBookingNoticeNote,
} from "@/app/components/BookingLimitNotes";

interface StylistPageAppointmentProps {
  userId: string;
  name: string;
  userPathId: string;
  services: (Service & { option?: ServiceOption })[];
  schedules: UserData["schedules"];
  vacations: UserData["vacations"];
  /**
   * Each business's cancellation policy, resolved server-side by the page. A
   * stylist can work at more than one business, so the policy is picked per
   * slot from the business that slot books into; a miss is `null` (unknown).
   */
  cancellationPolicies?: Partial<
    Record<EditionBusinessKey, BookingCancellationPolicy | null>
  >;
  /**
   * The stylist's customer booking window, resolved server-side by the page
   * from the locations they work at (`stylistCustomerBookingWindow`): the
   * loosest, so no location's bookable days are hidden. `null` = no cut.
   */
  bookingWindow?: CustomerBookingWindow | null;
  /**
   * The canonical membership's Openings tags: the review step's fallback
   * when there is no per-membership map. The page header shows no tags.
   */
  tags?: StylistTag[];
  /**
   * Tags per Openings member id (`UserData.membershipTags`). A slot books
   * under the membership of its business, so the review step shows that
   * membership's notes, never the canonical profile's for another business.
   */
  membershipTags?: Record<string, StylistTag[]>;
}
const StylistPageAppointment = ({
  userId,
  name,
  userPathId,
  services,
  schedules,
  vacations,
  cancellationPolicies,
  bookingWindow = null,
  tags,
  membershipTags,
}: StylistPageAppointmentProps) => {
  const { date, setDate, openAppointmentForm } = useApp();
  const openFormWithPolicy = React.useCallback(
    (form: AppointmentForm) => {
      const business = resolveBusinessForAppointment(form);
      // The slot's own membership (`form.userId`) decides the notes. With no
      // per-membership map (older data), the canonical tags apply only to
      // the canonical member; another membership shows none.
      const memberTags = membershipTags
        ? (membershipTags[form.userId] ?? [])
        : form.userId === userId
          ? (tags ?? [])
          : [];
      openAppointmentForm({
        ...form,
        cancellationPolicy: business
          ? (cancellationPolicies?.[business] ?? null)
          : null,
        memberTags,
      });
    },
    [openAppointmentForm, cancellationPolicies, tags, membershipTags, userId],
  );
  const maxDate = customerBookingWindowMaxDate(bookingWindow);
  // A date retained from another page may sit past this stylist's window.
  const effectiveDate = clampDateToBookingWindow(date, maxDate);
  const dateString = formatDate(effectiveDate);
  const [refreshCount, setRefreshCount] = useState(0);
  const [selectedServices, setSelectedServices] = useState<
    (Service & { option?: ServiceOption })[]
  >(
    services[0]
      ? [
          {
            ...services[0],
          },
        ]
      : [],
  );

  const workDays: number[] = [];
  schedules.forEach(({ hours }) => {
    hours.forEach((hour: string[], index: number) => {
      if (hour[2] === "on") {
        if (!workDays.includes(index)) {
          workDays.push(index);
        }
      }
    });
  });

  const convertedVacations = vacations
    ? vacations.flatMap((vacation) =>
        getDatesBetween(vacation.startDate, vacation.endDate),
      )
    : [];

  const isTodayOnVacation = convertedVacations.some(
    (_date) => dayjs(_date).format(DB_DATE_FORMAT) === dateString,
  );

  const filteredWorkDays = [0, 1, 2, 3, 4, 5, 6].filter(
    (day) => !workDays.includes(day),
  );

  return (
    <>
      {workDays.length !== 0 && (
        <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
          <section aria-labelledby="appointment-section">
            <p id="appointment-section" className="t-section">
              Appointment
            </p>
            <DateSelector
              selectedDate={effectiveDate}
              setDate={setDate}
              filteredWorkDays={filteredWorkDays}
              excludedDates={convertedVacations}
              hasBorder={true}
              maxDate={maxDate}
            />
            <CustomerBookingHorizonNote window={bookingWindow} />
          </section>

          {services.length > 0 && (
            <section aria-label="Service selection">
              <SelectService
                showPrice={true}
                defaultServices={services}
                selectedServices={selectedServices}
                setServices={(
                  services: (Service & { option?: ServiceOption })[],
                ) => setSelectedServices(services)}
              />
              <MinBookingNoticeNote services={selectedServices} />
            </section>
          )}

          <section aria-labelledby="schedule-display-section">
            <p id="schedule-display-section" className="t-section">
              Choose a time
            </p>
            <DisplaySchedules
              userId={userId}
              userName={name}
              userPathId={userPathId}
              isTodayOnVacation={isTodayOnVacation}
              date={dateString}
              services={selectedServices}
              locationMeta={schedules}
              refreshCount={refreshCount}
              onTimeClick={openFormWithPolicy}
              onRefresh={() => setRefreshCount(refreshCount + 1)}
            />
          </section>
        </Space>
      )}
    </>
  );
};

export default React.memo(StylistPageAppointment);
